import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createInitiative,
  deleteInitiative,
  getInitiative,
  getInitiativeOptions,
  getSelectableNodes,
  initiativeErrorMessage,
  listInitiatives,
  setInitiativeStatus,
  updateInitiative,
  uploadInitiativeAttachments,
} from "../services/efficiencyInitiativesApi";
import CompleteInitiativeDialog from "../components/CompleteInitiativeDialog";
import InitiativeAttachments from "../components/InitiativeAttachments";
import InitiativeImpactPanel from "../components/InitiativeImpactPanel";
import InitiativePerformancePanel from "../components/InitiativePerformancePanel";
import OverallImpactPanel from "../components/OverallImpactPanel";
import "./EfficiencyInitiativesTab.css";

/**
 * Tools -> Efficiency Initiatives.
 *
 * The initiative record itself — create, edit, complete, delete, and the
 * filterable table — plus three views over it:
 *
 *   All Initiatives  this table; the tiles here are the user's own EXPECTED
 *                    figures, labelled as such so they are never read as measured
 *   Performance      InitiativePerformancePanel — measured savings, ranked
 *   Overall Impact   OverallImpactPanel — portfolio totals, ROI, payback
 *
 * Measured impact is never stored: opening an initiative computes its before/after
 * energy comparison and its insight-loss attribution live (InitiativeImpactPanel,
 * InitiativeInsightLoss). That is why those panels own their own loading states
 * rather than the table waiting on them.
 */

const STATUS_ORDER = ["Planned", "Active", "Completed", "Cancelled"];

const NORMALIZATION_LABELS = {
  none: "None (compare raw kWh)",
  production: "Production output",
  operating_hours: "Operating hours",
  occupancy: "Occupancy",
  // Replaced the former "Weather" option: degree-days are just a custom driver
  // with a unit, and there is no site weather series in the database to read them
  // from, so a named Weather option promised a capability that did not exist.
  custom: "Custom",
};

/** Placeholder unit per driver. The user's own text always wins. */
const UNIT_PLACEHOLDER = {
  production: "tonnes",
  operating_hours: "hours",
  occupancy: "persons",
  custom: "e.g. degree-days",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  owner_name: "",
  owner_email: "",
  status: "Planned",
  meter_id: "",
  start_date: "",
  end_date: "",
  implemented_on: "",
  verified_on: "",
  implementation_cost: "",
  baseline_start: "",
  baseline_end: "",
  expected_annual_kwh_saving: "",
  expected_annual_cost_saving: "",
  normalization_variable: "none",
  normalization_baseline_value: "",
  normalization_after_value: "",
  normalization_unit: "",
  notes: "",
  insight_keys: [],
};

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });

const fmtMoney = (v) => (v == null ? "—" : `PKR ${PKR.format(v)}`);
const fmtKwh = (v) => (v == null ? "—" : `${PKR.format(v)} kWh`);
const fmtDate = (v) => (v ? v : "—");

/** ISO timestamp -> "12 Jun 2026, 14:05" without pulling in a date library. */
const fmtStamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "" -> null so the backend clears the column instead of failing date parsing. */
const orNull = (v) => (v === "" || v === undefined ? null : v);

/** "" -> null, otherwise a number. Guards against sending "" to a NUMERIC column. */
const orNullNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function EfficiencyInitiativesTab() {
  // Same session-lookup shape as ComplaintTicketTab, so a user resolves
  // identically across the Tools tabs.
  const username = useMemo(() => {
    let user = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) user = JSON.parse(stored);
    } catch (e) {
      user = null;
    }
    return (
      localStorage.getItem("username") ||
      user?.username ||
      user?.userName ||
      user?.email ||
      ""
    );
  }, []);

  // "table" = the initiative list; "performance" = rankings and outliers.
  const [view, setView] = useState("table");

  const [options, setOptions] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [filters, setFilters] = useState({
    status: "All",
    node_type: "All",
    insight_key: "All",
    q: "",
    from: "",
    to: "",
  });

  // null = closed. { mode: "create" } | { mode: "edit", initiative }
  const [formModal, setFormModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  // Identifies the newest detail request. A ref, not state: bumping it must not
  // cause a render, and the async handler needs to read the current value rather
  // than the one captured when it started.
  const detailRequest = useRef(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // The initiative whose Complete dialog is open, and that dialog's own error —
  // kept apart from `error` so a failed upload is shown inside the dialog rather
  // than behind it.
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeError, setCompleteError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const nodesById = useMemo(() => {
    const map = {};
    nodes.forEach((n) => {
      map[n.meter_id] = n;
    });
    return map;
  }, [nodes]);

  /* ------------------------------------------------------------------ load */

  // Options and nodes are static for the session; the list re-fetches on filter
  // change, so they are loaded separately to avoid refetching the catalogue on
  // every keystroke.
  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    (async () => {
      try {
        const [opts, nodeList] = await Promise.all([
          getInitiativeOptions(),
          getSelectableNodes(username),
        ]);
        if (cancelled) return;
        setOptions(opts);
        setNodes(nodeList);
      } catch (err) {
        if (!cancelled) setError(initiativeErrorMessage(err, "Could not load form options."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  const loadList = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listInitiatives(username, { ...filters, limit: 200 });
      setInitiatives(data.data || []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not load initiatives."));
      setInitiatives([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [username, filters]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  /* ------------------------------------------------------------------ form */

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormModal({ mode: "create" });
  };

  const openEdit = (initiative) => {
    setForm({
      title: initiative.title || "",
      description: initiative.description || "",
      owner_name: initiative.owner_name || "",
      owner_email: initiative.owner_email || "",
      status: initiative.status || "Planned",
      meter_id: initiative.meter_id || "",
      start_date: initiative.start_date || "",
      end_date: initiative.end_date || "",
      implemented_on: initiative.implemented_on || "",
      verified_on: initiative.verified_on || "",
      implementation_cost:
        initiative.implementation_cost == null ? "" : String(initiative.implementation_cost),
      baseline_start: initiative.baseline_start || "",
      baseline_end: initiative.baseline_end || "",
      expected_annual_kwh_saving:
        initiative.expected_annual_kwh_saving == null
          ? ""
          : String(initiative.expected_annual_kwh_saving),
      expected_annual_cost_saving:
        initiative.expected_annual_cost_saving == null
          ? ""
          : String(initiative.expected_annual_cost_saving),
      normalization_variable: initiative.normalization_variable || "none",
      normalization_baseline_value:
        initiative.normalization_baseline_value == null
          ? ""
          : String(initiative.normalization_baseline_value),
      normalization_after_value:
        initiative.normalization_after_value == null
          ? ""
          : String(initiative.normalization_after_value),
      normalization_unit: initiative.normalization_unit || "",
      notes: initiative.notes || "",
      insight_keys: (initiative.insights || []).map((i) => i.insight_key),
    });
    setFormError(null);
    setFormModal({ mode: "edit", initiative });
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  /**
   * Pick the initiative's one insight, replacing whatever was chosen before.
   *
   * Still stored as an array of one: the API, the link table and the
   * per-insight attribution rows are all plural, so the single-target rule is
   * enforced by this setter and by the server rather than by changing four
   * payload shapes.
   */
  const selectInsight = (key) => setForm((f) => ({ ...f, insight_keys: [key] }));

  /**
   * Client-side guards for the three rules the server also enforces. Checking here
   * keeps the common mistakes out of a network round trip; the server remains the
   * authority, so a bypassed check still fails safely.
   */
  const validate = () => {
    if (!form.title.trim()) return "Title is required.";
    if (!form.owner_name.trim()) return "Owner is required.";
    if (!form.meter_id) return "Select the affected node.";
    if (!form.start_date) return "Start date is required.";
    if (!form.insight_keys.length) return "Select the insight this initiative addresses.";
    if (form.end_date && form.end_date < form.start_date)
      return "End date cannot be earlier than the start date.";
    if (form.baseline_start && form.baseline_end && form.baseline_end < form.baseline_start)
      return "Baseline end cannot be earlier than baseline start.";
    if (form.baseline_end && form.baseline_end > form.start_date)
      return "The baseline period must end on or before the start date.";
    if (form.implementation_cost !== "" && Number(form.implementation_cost) < 0)
      return "Implementation cost cannot be negative.";
    return null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError(null);

    const payload = {
      username,
      title: form.title.trim(),
      description: orNull(form.description.trim()),
      owner_name: form.owner_name.trim(),
      owner_email: orNull(form.owner_email.trim()),
      status: form.status,
      meter_id: form.meter_id,
      start_date: form.start_date,
      end_date: orNull(form.end_date),
      implemented_on: orNull(form.implemented_on),
      verified_on: orNull(form.verified_on),
      implementation_cost: orNullNum(form.implementation_cost) ?? 0,
      baseline_start: orNull(form.baseline_start),
      baseline_end: orNull(form.baseline_end),
      expected_annual_kwh_saving: orNullNum(form.expected_annual_kwh_saving),
      expected_annual_cost_saving: orNullNum(form.expected_annual_cost_saving),
      normalization_variable: form.normalization_variable,
      // "" -> null, so an empty field means "not provided" and the backend falls
      // back to the raw comparison instead of receiving 0 and dividing by it.
      normalization_baseline_value:
        form.normalization_variable === "none"
          ? null
          : orNullNum(form.normalization_baseline_value),
      normalization_after_value:
        form.normalization_variable === "none"
          ? null
          : orNullNum(form.normalization_after_value),
      normalization_unit:
        form.normalization_variable === "none" ? null : orNull(form.normalization_unit),
      notes: orNull(form.notes.trim()),
      insight_keys: form.insight_keys,
    };

    try {
      if (formModal.mode === "create") {
        const created = await createInitiative(payload);
        setNotice(`Initiative ${created.reference_code} created.`);
      } else {
        await updateInitiative(formModal.initiative.initiative_id, payload);
        setNotice(`Initiative ${formModal.initiative.reference_code} updated.`);
      }
      setFormModal(null);
      await loadList();
    } catch (err) {
      setFormError(initiativeErrorMessage(err, "Could not save the initiative."));
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------ actions */

  /**
   * Complete an initiative, optionally attaching evidence and dates.
   *
   * The status transition goes first, because it is the action the user asked for.
   * If the upload then fails, the initiative IS completed and the message says so
   * and points at where to retry — reporting a plain failure would leave the user
   * thinking nothing happened and clicking Complete on a completed initiative.
   */
  const confirmComplete = async ({ files, note, implemented_on, verified_on }) => {
    const initiative = completeTarget;
    setBusyId(initiative.initiative_id);
    setCompleteError(null);
    setError(null);
    try {
      await setInitiativeStatus(initiative.initiative_id, {
        username,
        status: "Completed",
        implemented_on,
        verified_on,
        note: note || "Marked complete from the initiatives table.",
      });
    } catch (err) {
      setCompleteError(
        initiativeErrorMessage(err, "Could not complete the initiative.")
      );
      setBusyId(null);
      return;
    }

    let uploadWarning = null;
    if (files?.length) {
      try {
        await uploadInitiativeAttachments(
          initiative.initiative_id,
          username,
          files,
          note
        );
      } catch (err) {
        uploadWarning = initiativeErrorMessage(
          err,
          "the files could not be attached"
        );
      }
    }

    setCompleteTarget(null);
    setBusyId(null);
    if (uploadWarning) {
      setError(
        `${initiative.reference_code} was marked Completed, but ${uploadWarning} ` +
          "Open the initiative and use Supporting documents to try again."
      );
    } else {
      setNotice(
        `Initiative ${initiative.reference_code} marked Completed` +
          (files?.length ? ` with ${files.length} document(s) attached.` : ".")
      );
    }
    await loadList();
  };

  const handleDelete = async () => {
    const target = confirmDelete;
    setBusyId(target.initiative_id);
    setError(null);
    try {
      await deleteInitiative(target.initiative_id, username);
      setNotice(`Initiative ${target.reference_code} deleted.`);
      setConfirmDelete(null);
      await loadList();
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not delete the initiative."));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Open the detail modal on the row data the table already has, then fill in the
   * history and attachments.
   *
   * The response is applied only if it is still the one being waited for. Without
   * that check a slow reply landed after the user had closed the modal and
   * RE-OPENED it, and clicking View on two rows in quick succession could show the
   * first row's history under the second row's title.
   */
  const openDetail = async (initiative) => {
    const token = ++detailRequest.current;
    setDetailLoading(true);
    setDetailError(null);
    setDetail({ ...initiative, activity: [], attachments: [] });
    try {
      const full = await getInitiative(initiative.initiative_id, username);
      if (token !== detailRequest.current) return;
      setDetail(full);
    } catch (err) {
      if (token !== detailRequest.current) return;
      // Shown inside the modal, not behind it: the sections below would otherwise
      // report "No documents attached" and "No history recorded", which are
      // answers, and we do not have one.
      setDetailError(
        initiativeErrorMessage(err, "Could not load this initiative's history and documents.")
      );
    } finally {
      if (token === detailRequest.current) setDetailLoading(false);
    }
  };

  /**
   * Re-read the activity history alone, leaving everything else on screen.
   *
   * Guarded by the same request token as openDetail: if the modal has been closed
   * or switched to another initiative meanwhile, the reply is discarded.
   */
  const refreshDetailActivity = async () => {
    const target = detail?.initiative_id;
    if (!target) return;
    const token = detailRequest.current;
    try {
      const full = await getInitiative(target, username);
      if (token !== detailRequest.current) return;
      setDetail((d) =>
        d && d.initiative_id === target ? { ...d, activity: full.activity || [] } : d
      );
    } catch {
      // The history being one entry behind is not worth an error banner over an
      // upload that succeeded.
    }
  };

  const closeDetail = () => {
    // Bumping the token abandons any in-flight response, so it cannot re-open
    // the modal after the user has dismissed it.
    detailRequest.current += 1;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  /* ------------------------------------------------------------------ render */

  if (!username) {
    return (
      <div className="ei-container">
        <div className="ei-alert error">
          No signed-in user was found. Please sign in again.
        </div>
      </div>
    );
  }

  const insightGroups = options?.insights_by_category || {};
  // The chosen insight's display name, so the picker can confirm the selection in
  // words. Resolved from the grouped payload rather than stored in form state, to
  // keep one source of truth for the key.
  const selectedInsightName = Object.values(insightGroups)
    .flat()
    .find((i) => i.insight_key === form.insight_keys[0])?.name;

  return (
    <div className="ei-container">
      <div className="ei-header">
        <div className="ei-title-area">
          <h2>Efficiency Initiatives</h2>
          <p>
            Track energy-efficiency initiatives across buildings, sections and machines,
            and the insights each one targets.
          </p>
        </div>
        <div className="ei-header-actions">
          <div className="ei-view-toggle">
            <button
              className={`ei-toggle-btn${view === "table" ? " active" : ""}`}
              onClick={() => setView("table")}
            >
              All Initiatives
            </button>
            <button
              className={`ei-toggle-btn${view === "performance" ? " active" : ""}`}
              onClick={() => setView("performance")}
            >
              Performance
            </button>
            <button
              className={`ei-toggle-btn${view === "overall" ? " active" : ""}`}
              onClick={() => setView("overall")}
            >
              Overall Impact
            </button>
          </div>
          <button className="ei-primary-btn" onClick={openCreate} disabled={!options}>
            + New Initiative
          </button>
        </div>
      </div>

      {notice && <div className="ei-alert success">{notice}</div>}
      {error && <div className="ei-alert error">{error}</div>}

      {/* Header tiles describe everything the current filters match, not just the
          visible rows — the summary is computed server-side over the filtered set. */}
      {view === "overall" ? (
        <OverallImpactPanel username={username} statusFilter={filters.status} />
      ) : view === "performance" ? (
        <InitiativePerformancePanel
          username={username}
          statusFilter={filters.status}
        />
      ) : (
        <>
      {summary && (
        <div className="ei-stats">
          <div className="ei-stat-card">
            <p className="ei-stat-label">Total Initiatives</p>
            <p className="ei-stat-value">{summary.total_initiatives}</p>
            <p className="ei-stat-sub">{summary.planned} planned</p>
          </div>
          <div className="ei-stat-card">
            <p className="ei-stat-label">Active</p>
            <p className="ei-stat-value">{summary.active}</p>
            <p className="ei-stat-sub">currently in progress</p>
          </div>
          <div className="ei-stat-card">
            <p className="ei-stat-label">Completed</p>
            <p className="ei-stat-value">{summary.completed}</p>
            <p className="ei-stat-sub">{summary.cancelled} cancelled</p>
          </div>
          <div className="ei-stat-card">
            <p className="ei-stat-label">Implementation Cost</p>
            <p className="ei-stat-value">{fmtMoney(summary.total_implementation_cost)}</p>
            <p className="ei-stat-sub">across all matching initiatives</p>
          </div>
          <div className="ei-stat-card ei-stat-expected">
            <p className="ei-stat-label">Expected Annual Saving</p>
            <p className="ei-stat-value">{fmtKwh(summary.expected_annual_kwh_saving)}</p>
            <p className="ei-stat-sub">
              estimate · {fmtMoney(summary.expected_annual_cost_saving)}
            </p>
          </div>
        </div>
      )}

      <div className="ei-card">
        <div className="ei-filters">
          <div className="ei-field">
            <label htmlFor="ei-f-status">Status</label>
            <select
              id="ei-f-status"
              className="ei-select"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="All">All statuses</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="ei-field">
            <label htmlFor="ei-f-node">Node type</label>
            <select
              id="ei-f-node"
              className="ei-select"
              value={filters.node_type}
              onChange={(e) => setFilters((f) => ({ ...f, node_type: e.target.value }))}
            >
              <option value="All">All nodes</option>
              <option value="Building">Building</option>
              <option value="Section">Section</option>
              <option value="Machine">Machine</option>
            </select>
          </div>

          <div className="ei-field">
            <label htmlFor="ei-f-insight">Insight</label>
            <select
              id="ei-f-insight"
              className="ei-select"
              value={filters.insight_key}
              onChange={(e) => setFilters((f) => ({ ...f, insight_key: e.target.value }))}
            >
              <option value="All">All insights</option>
              {(options?.insights || []).map((i) => (
                <option key={i.insight_key} value={i.insight_key}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ei-field">
            <label htmlFor="ei-f-from">Starting from</label>
            <input
              id="ei-f-from"
              type="date"
              className="ei-input"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            />
          </div>

          <div className="ei-field">
            <label htmlFor="ei-f-to">Starting up to</label>
            <input
              id="ei-f-to"
              type="date"
              className="ei-input"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            />
          </div>

          <div className="ei-field">
            <label htmlFor="ei-f-q">Search</label>
            <input
              id="ei-f-q"
              type="text"
              className="ei-input"
              placeholder="Title, code or owner"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
          </div>
        </div>

        {loading ? (
          <div className="ei-empty">Loading initiatives…</div>
        ) : initiatives.length === 0 ? (
          <div className="ei-empty">
            <strong>No initiatives yet</strong>
            Create one to start tracking the impact of an efficiency change.
          </div>
        ) : (
          <div className="ei-table-wrapper">
            <table className="ei-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Initiative</th>
                  <th>Affected Node</th>
                  <th>Insights</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Start</th>
                  <th className="ei-num">Cost (PKR)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {initiatives.map((it) => {
                  const busy = busyId === it.initiative_id;
                  const shown = it.insights.slice(0, 2);
                  const extra = it.insights.length - shown.length;
                  return (
                    <tr key={it.initiative_id} className="ei-row">
                      <td className="ei-ref">{it.reference_code}</td>
                      <td>
                        <div className="ei-title-cell">{it.title}</div>
                        {it.description && (
                          <div className="ei-title-sub">{it.description}</div>
                        )}
                      </td>
                      <td>
                        <span className={`ei-badge node-${it.node_type}`}>
                          {it.node_type}
                        </span>
                        <div className="ei-title-sub">{it.node_name || it.meter_id}</div>
                      </td>
                      <td>
                        <div className="ei-chips">
                          {shown.map((i) => (
                            <span className="ei-chip" key={i.insight_key}>
                              {i.name}
                            </span>
                          ))}
                          {extra > 0 && <span className="ei-chip more">+{extra}</span>}
                        </div>
                      </td>
                      <td>{it.owner_name}</td>
                      <td>
                        <span className={`ei-badge status-${it.status}`}>{it.status}</span>
                      </td>
                      <td>{fmtDate(it.start_date)}</td>
                      <td className="ei-num">
                        {it.implementation_cost == null
                          ? "—"
                          : PKR.format(it.implementation_cost)}
                      </td>
                      <td>
                        <div className="ei-actions">
                          <button
                            className="ei-action-btn"
                            onClick={() => openDetail(it)}
                            disabled={busy}
                          >
                            View
                          </button>
                          <button
                            className="ei-action-btn"
                            onClick={() => openEdit(it)}
                            disabled={busy}
                          >
                            Edit
                          </button>
                          <button
                            className="ei-action-btn complete"
                            onClick={() => {
                              setCompleteError(null);
                              setCompleteTarget(it);
                            }}
                            /* Already-completed and cancelled initiatives have no
                               "complete" transition left to make. */
                            disabled={
                              busy || it.status === "Completed" || it.status === "Cancelled"
                            }
                            title={
                              it.status === "Completed"
                                ? "Already completed"
                                : it.status === "Cancelled"
                                ? "Cancelled initiatives cannot be completed"
                                : "Mark this initiative complete"
                            }
                          >
                            Complete
                          </button>
                          <button
                            className="ei-action-btn danger"
                            onClick={() => setConfirmDelete(it)}
                            disabled={busy}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {/* ---------------------------------------------------------- form modal */}
      {formModal && (
        <div className="ei-modal-overlay" onClick={() => !saving && setFormModal(null)}>
          <div className="ei-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ei-modal-header">
              <h3>
                {formModal.mode === "create"
                  ? "New Initiative"
                  : `Edit ${formModal.initiative.reference_code}`}
              </h3>
              <button
                className="ei-modal-close"
                onClick={() => setFormModal(null)}
                disabled={saving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="ei-modal-body">
              {formError && <div className="ei-alert error">{formError}</div>}

              <div className="ei-form-grid">
                <div className="ei-section-heading">Initiative</div>

                <div className="ei-field span-2">
                  <label htmlFor="ei-title">
                    Title <span className="ei-required">*</span>
                  </label>
                  <input
                    id="ei-title"
                    className="ei-input"
                    value={form.title}
                    maxLength={200}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="e.g. VFD installed on Compressor 2"
                  />
                </div>

                <div className="ei-field span-2">
                  <label htmlFor="ei-desc">Description</label>
                  <textarea
                    id="ei-desc"
                    className="ei-textarea"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="What was changed, and why"
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-owner">
                    Owner <span className="ei-required">*</span>
                  </label>
                  <input
                    id="ei-owner"
                    className="ei-input"
                    value={form.owner_name}
                    maxLength={120}
                    onChange={(e) => setField("owner_name", e.target.value)}
                    placeholder="Responsible person"
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-owner-email">Owner email</label>
                  <input
                    id="ei-owner-email"
                    type="email"
                    className="ei-input"
                    value={form.owner_email}
                    onChange={(e) => setField("owner_email", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-status">Status</label>
                  <select
                    id="ei-status"
                    className="ei-select"
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    {STATUS_ORDER.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-node">
                    Affected node <span className="ei-required">*</span>
                  </label>
                  <select
                    id="ei-node"
                    className="ei-select"
                    value={form.meter_id}
                    onChange={(e) => setField("meter_id", e.target.value)}
                  >
                    <option value="">Select a node…</option>
                    {nodes.map((n) => (
                      <option key={n.meter_id} value={n.meter_id}>
                        {/* Non-breaking spaces indent by hierarchy depth: <option>
                            cannot be styled, so the tree shape has to be in the text. */}
                        {" ".repeat(n.depth * 2)}
                        {n.node_name} — {n.node_type}
                      </option>
                    ))}
                  </select>
                  {form.meter_id && nodesById[form.meter_id] && (
                    <p className="ei-hint">
                      {nodesById[form.meter_id].node_type} ·{" "}
                      {nodesById[form.meter_id].category || "—"}
                    </p>
                  )}
                </div>

                <div className="ei-section-heading">Affected insight</div>

                <div className="ei-field span-2">
                  <label>
                    Select the insight this initiative addresses{" "}
                    <span className="ei-required">*</span>
                  </label>
                  {/* Radios, not checkboxes: an initiative targets exactly one
                      insight. A radio group makes that a property of the control
                      rather than a rule the user discovers on submit, and picking
                      a second option replaces the first instead of erroring.
                      One `name` across every group so all 31 are mutually
                      exclusive — per-group names would allow one pick per
                      category, which is the bug this replaces. */}
                  <div className="ei-insight-picker">
                    {Object.keys(insightGroups).length === 0 && (
                      <p className="ei-hint">Loading insights…</p>
                    )}
                    {Object.entries(insightGroups).map(([category, items]) => (
                      <div className="ei-insight-group" key={category}>
                        <div className="ei-insight-group-label">{category}</div>
                        {items.map((i) => (
                          <label className="ei-insight-option" key={i.insight_key}>
                            <input
                              type="radio"
                              name="ei-insight"
                              value={i.insight_key}
                              checked={form.insight_keys[0] === i.insight_key}
                              onChange={() => selectInsight(i.insight_key)}
                            />
                            {i.name}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                  <p className="ei-selected-count">
                    {selectedInsightName || "None selected"}
                  </p>
                </div>

                <div className="ei-section-heading">Timing &amp; cost</div>

                <div className="ei-field">
                  <label htmlFor="ei-start">
                    Start date <span className="ei-required">*</span>
                  </label>
                  <input
                    id="ei-start"
                    type="date"
                    className="ei-input"
                    value={form.start_date}
                    onChange={(e) => setField("start_date", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-end">End date</label>
                  <input
                    id="ei-end"
                    type="date"
                    className="ei-input"
                    value={form.end_date}
                    onChange={(e) => setField("end_date", e.target.value)}
                  />
                  <p className="ei-hint">Optional</p>
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-impl">Implemented on</label>
                  <input
                    id="ei-impl"
                    type="date"
                    className="ei-input"
                    value={form.implemented_on}
                    onChange={(e) => setField("implemented_on", e.target.value)}
                  />
                  <p className="ei-hint">Splits &quot;before&quot; from &quot;after&quot;</p>
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-verified">Verified on</label>
                  <input
                    id="ei-verified"
                    type="date"
                    className="ei-input"
                    value={form.verified_on}
                    onChange={(e) => setField("verified_on", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-cost">Implementation cost (PKR)</label>
                  <input
                    id="ei-cost"
                    type="number"
                    min="0"
                    step="1"
                    className="ei-input"
                    value={form.implementation_cost}
                    onChange={(e) => setField("implementation_cost", e.target.value)}
                  />
                </div>

                <div className="ei-section-heading">Baseline &amp; expected savings</div>

                <div className="ei-field">
                  <label htmlFor="ei-bstart">Baseline start</label>
                  <input
                    id="ei-bstart"
                    type="date"
                    className="ei-input"
                    value={form.baseline_start}
                    onChange={(e) => setField("baseline_start", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-bend">Baseline end</label>
                  <input
                    id="ei-bend"
                    type="date"
                    className="ei-input"
                    value={form.baseline_end}
                    onChange={(e) => setField("baseline_end", e.target.value)}
                  />
                  <p className="ei-hint">Must end on or before the start date</p>
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-exp-kwh">Expected annual saving (kWh)</label>
                  <input
                    id="ei-exp-kwh"
                    type="number"
                    step="any"
                    className="ei-input"
                    value={form.expected_annual_kwh_saving}
                    onChange={(e) => setField("expected_annual_kwh_saving", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-exp-pkr">Expected annual saving (PKR)</label>
                  <input
                    id="ei-exp-pkr"
                    type="number"
                    step="any"
                    className="ei-input"
                    value={form.expected_annual_cost_saving}
                    onChange={(e) => setField("expected_annual_cost_saving", e.target.value)}
                  />
                </div>

                <div className="ei-field">
                  <label htmlFor="ei-norm">Normalise savings against</label>
                  <select
                    id="ei-norm"
                    className="ei-select"
                    value={form.normalization_variable}
                    onChange={(e) => setField("normalization_variable", e.target.value)}
                  >
                    {(options?.normalization_variables || ["none"]).map((v) => (
                      <option key={v} value={v}>
                        {NORMALIZATION_LABELS[v] || v}
                      </option>
                    ))}
                  </select>
                </div>

                {/* The two values, revealed only once a driver is chosen.
                    Choosing a driver does nothing on its own — production, hours
                    and occupancy are not in the meter data, so these numbers can
                    only come from the user. Left empty, savings fall back to the
                    plain before/after comparison; nothing is assumed. */}
                {form.normalization_variable !== "none" && (
                  <div className="ei-field ei-norm-fields">
                    <label htmlFor="ei-norm-before">
                      {NORMALIZATION_LABELS[form.normalization_variable]} — before vs
                      after
                    </label>
                    <div className="ei-norm-inputs">
                      <input
                        id="ei-norm-before"
                        type="number"
                        step="any"
                        min="0"
                        className="ei-input"
                        placeholder="Baseline total"
                        value={form.normalization_baseline_value}
                        onChange={(e) =>
                          setField("normalization_baseline_value", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        step="any"
                        min="0"
                        className="ei-input"
                        placeholder="Since implementation"
                        value={form.normalization_after_value}
                        onChange={(e) =>
                          setField("normalization_after_value", e.target.value)
                        }
                      />
                      <input
                        type="text"
                        className="ei-input ei-norm-unit"
                        placeholder={
                          UNIT_PLACEHOLDER[form.normalization_variable] || "unit"
                        }
                        value={form.normalization_unit}
                        onChange={(e) => setField("normalization_unit", e.target.value)}
                      />
                    </div>
                    <p className="ei-hint">
                      Period totals. Blank compares raw kWh.
                    </p>
                  </div>
                )}

                <div className="ei-field span-2">
                  <label htmlFor="ei-notes">Notes &amp; implementation details</label>
                  <textarea
                    id="ei-notes"
                    className="ei-textarea"
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="ei-modal-footer">
              <button
                className="ei-secondary-btn"
                onClick={() => setFormModal(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button className="ei-primary-btn" onClick={handleSave} disabled={saving}>
                {saving
                  ? "Saving…"
                  : formModal.mode === "create"
                  ? "Create Initiative"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------- detail modal */}
      {detail && (
        <div className="ei-modal-overlay" onClick={closeDetail}>
          <div className="ei-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ei-modal-header">
              <h3>
                {detail.reference_code} · {detail.title}
                {/* The modal opens on the row data the table already has, so the
                    chip marks that the rest is still arriving rather than holding
                    the whole dialog behind a spinner. */}
                {detailLoading && (
                  <span className="ei-loading-chip" role="status">
                    <span className="ei-spinner" aria-hidden="true" />
                    Loading details
                  </span>
                )}
              </h3>
              <button
                className="ei-modal-close"
                onClick={closeDetail}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="ei-modal-body">
              {detailError && (
                <div className="ei-alert error" style={{ marginBottom: "1rem" }}>
                  {detailError}
                </div>
              )}
              <div className="ei-detail-grid">
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Status</p>
                  <p className="ei-detail-value">
                    <span className={`ei-badge status-${detail.status}`}>
                      {detail.status}
                    </span>
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Owner</p>
                  <p className="ei-detail-value">
                    {detail.owner_name}
                    {detail.owner_email ? ` · ${detail.owner_email}` : ""}
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Affected node</p>
                  <p className="ei-detail-value">
                    {detail.node_name || detail.meter_id} ({detail.node_type})
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Start · End</p>
                  <p className="ei-detail-value">
                    {fmtDate(detail.start_date)} → {fmtDate(detail.end_date)}
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Implemented · Verified</p>
                  <p className="ei-detail-value">
                    {fmtDate(detail.implemented_on)} · {fmtDate(detail.verified_on)}
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Baseline period</p>
                  <p className="ei-detail-value">
                    {detail.baseline_start || detail.baseline_end
                      ? `${fmtDate(detail.baseline_start)} → ${fmtDate(detail.baseline_end)}`
                      : "Derived automatically"}
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Implementation cost</p>
                  <p className="ei-detail-value">{fmtMoney(detail.implementation_cost)}</p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Expected annual saving</p>
                  <p className="ei-detail-value">
                    {fmtKwh(detail.expected_annual_kwh_saving)} ·{" "}
                    {fmtMoney(detail.expected_annual_cost_saving)}
                  </p>
                </div>
                <div className="ei-detail-item">
                  <p className="ei-detail-label">Normalised against</p>
                  <p className="ei-detail-value">
                    {NORMALIZATION_LABELS[detail.normalization_variable] ||
                      detail.normalization_variable}
                  </p>
                </div>
              </div>

              <div className="ei-detail-item" style={{ marginBottom: "1.25rem" }}>
                <p className="ei-detail-label">Affected insight</p>
                <div className="ei-chips" style={{ maxWidth: "100%", marginTop: "0.3rem" }}>
                  {(detail.insights || []).map((i) => (
                    <span className="ei-chip" key={i.insight_key}>
                      {i.name}
                    </span>
                  ))}
                </div>
              </div>

              {detail.description && (
                <div className="ei-detail-item" style={{ marginBottom: "1rem" }}>
                  <p className="ei-detail-label">Description</p>
                  <p className="ei-detail-value">{detail.description}</p>
                </div>
              )}

              {detail.notes && (
                <div className="ei-detail-item" style={{ marginBottom: "1.25rem" }}>
                  <p className="ei-detail-label">Notes</p>
                  <p className="ei-detail-value">{detail.notes}</p>
                </div>
              )}


              {/* Evidence sits above the measured impact: it is part of the record,
                  not part of the calculation, and the impact panel is long. */}
              <InitiativeAttachments
                initiative={detail}
                username={username}
                attachments={detail.attachments || []}
                loading={detailLoading}
                failed={Boolean(detailError)}
                onChange={(attachments) => {
                  setDetail((d) => (d ? { ...d, attachments } : d));
                  // Attaching and removing both write an activity row, so the
                  // history two sections down is now stale. Refetched quietly —
                  // without touching detailLoading, which would flash the
                  // skeletons over content the user is already reading.
                  void refreshDetailActivity();
                }}
              />

              <InitiativeImpactPanel
                initiative={detail}
                username={username}
              />

              <p className="ei-detail-label">Activity history</p>
              {detailLoading ? (
                <>
                  <p className="ei-loading-row" role="status">
                    <span className="ei-spinner" aria-hidden="true" />
                    Loading history…
                  </p>
                  <span className="ei-skeleton w-60" />
                  <span className="ei-skeleton w-80" />
                  <span className="ei-skeleton w-40" />
                </>
              ) : detailError ? (
                <p className="ei-hint">History could not be loaded.</p>
              ) : (
                <ul className="ei-timeline">
                  {(detail.activity || []).map((a) => (
                    <li key={a.activity_id}>
                      <strong>{a.action.replace(/_/g, " ")}</strong>
                      {a.note ? ` — ${a.note}` : ""}
                      <div className="ei-timeline-meta">
                        {a.username} · {fmtStamp(a.created_at)}
                      </div>
                      {a.field_changes && Object.keys(a.field_changes).length > 0 && (
                        <div className="ei-timeline-change">
                          {Object.entries(a.field_changes)
                            .map(
                              ([field, c]) =>
                                `${field}: ${JSON.stringify(c.from)} → ${JSON.stringify(
                                  c.to
                                )}`
                            )
                            .join("  ·  ")}
                        </div>
                      )}
                    </li>
                  ))}
                  {(detail.activity || []).length === 0 && (
                    <p className="ei-hint">No history recorded.</p>
                  )}
                </ul>
              )}
            </div>

            <div className="ei-modal-footer">
              <button className="ei-secondary-btn" onClick={closeDetail}>
                Close
              </button>
              <button
                className="ei-primary-btn"
                onClick={() => {
                  const target = detail;
                  closeDetail();
                  openEdit(target);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------- complete dialog */}
      {completeTarget && (
        <CompleteInitiativeDialog
          initiative={completeTarget}
          busy={busyId === completeTarget.initiative_id}
          error={completeError}
          onCancel={() => {
            setCompleteTarget(null);
            setCompleteError(null);
          }}
          onConfirm={confirmComplete}
        />
      )}

      {/* ---------------------------------------------------------- delete confirm */}
      {confirmDelete && (
        <div className="ei-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="ei-modal narrow" onClick={(e) => e.stopPropagation()}>
            <div className="ei-modal-header">
              <h3>Delete initiative?</h3>
              <button
                className="ei-modal-close"
                onClick={() => setConfirmDelete(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="ei-modal-body">
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#475569" }}>
                <strong>{confirmDelete.reference_code}</strong> — {confirmDelete.title}
              </p>
              <p style={{ marginTop: "0.75rem", fontSize: "0.82rem", color: "#64748b" }}>
                Removed from the table; the record is kept so past savings stay
                reproducible.
              </p>
            </div>
            <div className="ei-modal-footer">
              <button
                className="ei-secondary-btn"
                onClick={() => setConfirmDelete(null)}
                disabled={busyId === confirmDelete.initiative_id}
              >
                Cancel
              </button>
              <button
                className="ei-primary-btn"
                onClick={handleDelete}
                disabled={busyId === confirmDelete.initiative_id}
                style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)" }}
              >
                {busyId === confirmDelete.initiative_id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
